import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";

type Fit = "cover" | "contain";
const SIBLINGS: Array<{
  name: string;
  tagline: string;
  url: string;
  wordmark: string;
  fit: Fit;
  accent: "sky" | "peach" | "mint";
  bg: string;
}> = [
  {
    name: "Chaosology",
    tagline: "Future-Driven Technology, Today's Solution.",
    url: "https://chaosology.com",
    wordmark: "/logos/chaosology.gif",
    fit: "contain",
    accent: "sky",
    bg: "from-sky-100 via-white to-lav-50",
  },
  {
    name: "Boreology",
    tagline: "Covenant Candles & Hosts — premium church supplies.",
    url: "https://boreology.com",
    wordmark: "/logos/boreology.svg",
    fit: "contain",
    accent: "peach",
    bg: "from-peach-50 via-white to-rose-50",
  },
  {
    name: "Lectureology",
    tagline: "UK Diplomas, K–12 Tuition & Test Prep, Globally.",
    url: "https://lectureology.com",
    wordmark: "/logos/lectureology-final.svg",
    fit: "contain",
    accent: "mint",
    bg: "from-mint-50 via-white to-sky-50",
  },
];

const JOURNEYS = [
  {
    to: "/elder-sanctuary",
    eyebrow: "For a homeless elder",
    title: "Elder Sanctuary",
    body:
      "Shelter, three meals a day, medicine and community — for older people with no family and nowhere to go. Free, for life.",
    cta: "Join the Elder waitlist",
    accent: "peach",
    gradient: "from-peach-100 via-white to-rose-50",
    orb: "#ffc6a3",
  },
  {
    to: "/orphan-sanctuary",
    eyebrow: "For an orphaned child",
    title: "Orphan Sanctuary",
    body:
      "A safe home, three meals, schooling and tutoring for children without parents or guardians. With full respect for child-welfare law.",
    cta: "Join the Orphan waitlist",
    accent: "sky",
    gradient: "from-sky-100 via-white to-lav-50",
    orb: "#b0c7ff",
  },
  {
    to: "/volunteer",
    eyebrow: "Give your time",
    title: "Volunteer with us",
    body:
      "We don't fundraise. What we need are humans — doctors, tutors, cooks, drivers, neighbours. Remote or in person.",
    cta: "Register to volunteer",
    accent: "mint",
    gradient: "from-mint-100 via-white to-butter-50",
    orb: "#aee8c7",
  },
] as const;

export default function Home() {
  return (
    <>
      {/* ============= HERO ============= */}
      <section className="relative pt-14 sm:pt-20 pb-4">
        <div
          aria-hidden
          className="orb top-10 -left-20 h-80 w-80 animate-float"
          style={{ background: "radial-gradient(closest-side, #d7c8ff, transparent)" }}
        />
        <div
          aria-hidden
          className="orb top-32 right-0 h-96 w-96 animate-float"
          style={{ background: "radial-gradient(closest-side, #ffc6a3, transparent)", animationDelay: "-2s" }}
        />
        <div
          aria-hidden
          className="orb bottom-0 left-1/3 h-80 w-80 animate-float"
          style={{ background: "radial-gradient(closest-side, #aee8c7, transparent)", animationDelay: "-4s" }}
        />

        <div className="container-xl text-center">
          <Reveal>
            <span className="pill pill-lav">Sanctuary of hope · Tirunelveli, India</span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl sm:text-7xl lg:text-[92px] leading-[0.95] tracking-tight text-balance max-w-5xl mx-auto">
              Dignified shelter for those the <em className="not-italic grad-text">world has left behind.</em>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 text-lg sm:text-xl text-ink-500 leading-relaxed max-w-2xl mx-auto">
              Sanctology Welfare Foundation offers sanctuary to homeless elders
              and orphaned children — quietly, and without asking anyone for
              anything. The CSR arm of Logy Holdings.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Link to="/elder-sanctuary" className="btn-primary">
                Refer an elder →
              </Link>
              <Link to="/orphan-sanctuary" className="btn-ghost">
                Refer a child
              </Link>
              <Link to="/volunteer" className="btn-ghost">
                Volunteer
              </Link>
            </div>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400">
              Funded by · Chaosology · Boreology · Lectureology
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============= THREE JOURNEYS ============= */}
      <section className="container-xl pt-16 sm:pt-24 pb-8">
        <Reveal>
          <div className="max-w-2xl mx-auto text-center mb-10">
            <span className="pill pill-teal">Three ways in</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-balance">
              Whoever you are, start here.
            </h2>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {JOURNEYS.map((j, i) => (
            <Reveal key={j.to} delay={i * 100}>
              <Link
                to={j.to}
                className={`group relative block rounded-3xl overflow-hidden border border-white/90 glass-lift h-full p-7 sm:p-8 bg-gradient-to-br ${j.gradient}`}
              >
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition"
                  style={{ background: `radial-gradient(closest-side, ${j.orb}, transparent)` }}
                />
                <div className="relative">
                  <div className={`pill pill-${j.accent}`}>{j.eyebrow}</div>
                  <h3 className="font-display text-3xl mt-4">{j.title}</h3>
                  <p className="mt-3 text-ink-500 leading-relaxed">{j.body}</p>
                  <div className="mt-6 inline-flex items-center gap-1 font-medium text-ink-900 group-hover:gap-2 transition-all">
                    {j.cta}
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============= PROMISE ============= */}
      <section className="container-xl py-20 sm:py-28">
        <Reveal>
          <div className="glass-strong rounded-3xl p-8 sm:p-16 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-24 -left-24 h-80 w-80 rounded-full blur-3xl opacity-40"
              style={{ background: "radial-gradient(closest-side, #5fcfc9, transparent)" }}
            />
            <div
              aria-hidden
              className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-40"
              style={{ background: "radial-gradient(closest-side, #d7c8ff, transparent)" }}
            />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5">
                <span className="pill">The Sanctology promise</span>
                <h2 className="mt-4 font-display text-4xl sm:text-5xl">
                  Never ask, <span className="grad-text">always welcome.</span>
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="text-ink-700 text-lg leading-relaxed">
                  We do not charge the people we serve. We do not run donation
                  campaigns. We do not post "inspiration stories" on social
                  media. Sanctology exists because our sibling operating
                  companies fund its work — so the people we serve are never
                  asked, and care is never conditional on a story.
                </p>
                <Link to="/support" className="hover-link mt-6 inline-flex">
                  How the funding works →
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============= LOGY FAMILY ============= */}
      <section className="container-xl pb-20 sm:pb-28">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="pill pill-sky">The Logy family</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl">
              The companies that make this possible.
            </h2>
            <p className="mt-4 text-ink-500 leading-relaxed">
              Sanctology runs on profits from three sibling operating companies
              under Logy Holdings. The best way to support us is to work with
              them.
            </p>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {SIBLINGS.map((s, i) => (
            <Reveal key={s.name} delay={i * 100}>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className={`group block relative overflow-hidden rounded-3xl border border-white/90 glass-lift h-full bg-gradient-to-br ${s.bg}`}
              >
                <div className="aspect-[16/9] bg-white/70 border-b border-white flex items-center justify-center overflow-hidden">
                  <img
                    src={s.wordmark}
                    alt={`${s.name} logo`}
                    className={
                      s.fit === "cover"
                        ? "w-full h-full object-cover"
                        : "max-w-[80%] max-h-[70%] object-contain"
                    }
                    loading="lazy"
                  />
                </div>
                <div className="p-6 sm:p-7">
                  <div className={`pill pill-${s.accent}`}>{s.name}</div>
                  <p className="text-ink-700 mt-3 leading-relaxed">{s.tagline}</p>
                  <div className="mt-5 inline-flex items-center gap-1 font-medium text-royal group-hover:gap-2 transition-all">
                    Visit {s.name.toLowerCase()}.com
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M7 17L17 7M17 7H9M17 7V15" />
                    </svg>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
